import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  tenantNotificationsApi,
  type TenantNotification,
  type TenantNotificationInbox,
} from '@/core/api/tenantNotificationsApi';
import { useAuthStore } from '@/core/auth/authStore';
import { getErrorMessage } from '@/core/utils/errors';
import { AppIcon } from '@/shared/components/AppIcon';

const formatDate = (value?: string | null): string => {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? '—'
    : new Intl.DateTimeFormat('fa-IR-u-ca-persian', {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(parsed);
};

const categoryStyle = (category: TenantNotification['category'], notificationType: TenantNotification['notificationType']): { label: string; card: string; badge: string } => {
  if (notificationType === 'reservation-requested') return { label: 'درخواست رزرو', card: 'border-violet-200 bg-violet-50/60', badge: 'bg-violet-100 text-violet-700' };
  if (category === 'critical') return { label: 'فوری', card: 'border-rose-200 bg-rose-50/60', badge: 'bg-rose-100 text-rose-700' };
  if (category === 'warning') return { label: 'هشدار', card: 'border-amber-200 bg-amber-50/60', badge: 'bg-amber-100 text-amber-700' };
  return { label: 'اطلاع‌رسانی', card: 'border-sky-200 bg-sky-50/60', badge: 'bg-sky-100 text-sky-700' };
};

export const TenantNotificationBell = (): React.ReactElement => {
  const queryClient = useQueryClient();
  const tenantId = useAuthStore((state) => state.tenant?.id ?? null);
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const queryKey = ['tenant-notifications', tenantId] as const;

  const inboxQuery = useQuery({
    queryKey,
    queryFn: () => tenantNotificationsApi.list(50),
    enabled: Boolean(tenantId),
    refetchInterval: 20_000,
    staleTime: 10_000,
    // A user may sign out of one company and immediately sign in to another
    // in the same browser tab. Always load the target company's inbox instead
    // of briefly rendering notifications cached for the previous tenant.
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
  });

  const viewMutation = useMutation({
    mutationFn: tenantNotificationsApi.markViewed,
    onSuccess: (updated) => {
      queryClient.setQueryData<TenantNotificationInbox>(queryKey, (current) => {
        if (!current) return current;
        const wasUnread = current.items.some((item) => item.id === updated.id && !item.isViewed);
        return {
          unreadCount: Math.max(0, current.unreadCount - (wasUnread ? 1 : 0)),
          items: current.items.map((item) => (item.id === updated.id ? updated : item)),
        };
      });
    },
  });

  useEffect(() => {
    if (!open) return undefined;
    const closeOnOutside = (event: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  const openItem = (item: TenantNotification): void => {
    setExpandedId((current) => (current === item.id ? null : item.id));
    if (!item.isViewed && !viewMutation.isPending) viewMutation.mutate(item.id);
  };

  const unreadCount = inboxQuery.data?.unreadCount ?? 0;

  return (
    <div className="relative" ref={rootRef}>
      <button
        aria-expanded={open}
        aria-label={unreadCount > 0 ? `${unreadCount.toLocaleString('fa-IR')} اعلان خوانده‌نشده` : 'اعلان‌های سازمان'}
        className={`relative grid size-10 place-items-center rounded-xl border transition ${open ? 'border-indigo-200 bg-indigo-50 text-indigo-600' : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}
        onClick={() => setOpen((value) => !value)}
        type="button"
      >
        <AppIcon name="bell" size={19} />
        {unreadCount > 0 ? (
          <span className="absolute -left-1.5 -top-1.5 grid min-w-5 place-items-center rounded-full border-2 border-white bg-rose-500 px-1 text-[9px] font-black leading-4 text-white">
            {unreadCount > 99 ? '+۹۹' : unreadCount.toLocaleString('fa-IR')}
          </span>
        ) : null}
      </button>

      {open ? (
        <section className="fixed inset-x-3 top-[78px] z-[70] overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl shadow-slate-950/20 sm:absolute sm:inset-x-auto sm:left-0 sm:top-12 sm:w-[28rem]" aria-label="اعلان‌های سازمان">
          <header className="flex items-center justify-between gap-3 border-b border-slate-100 bg-gradient-to-l from-indigo-50 to-white px-4 py-4">
            <div>
              <h2 className="text-sm font-black text-slate-900">اعلان‌های سازمان</h2>
              <p className="mt-1 text-[11px] text-slate-400">{unreadCount > 0 ? `${unreadCount.toLocaleString('fa-IR')} اعلان خوانده‌نشده` : 'همه اعلان‌ها مشاهده شده‌اند'}</p>
            </div>
            <div className="flex items-center gap-1">
              <button aria-label="بروزرسانی اعلان‌ها" className="grid size-8 place-items-center rounded-lg text-slate-400 hover:bg-white hover:text-indigo-600 disabled:opacity-50" disabled={inboxQuery.isFetching} onClick={() => void inboxQuery.refetch()} type="button"><AppIcon name="activity" size={16} /></button>
              <button aria-label="بستن اعلان‌ها" className="grid size-8 place-items-center rounded-lg text-slate-400 hover:bg-white hover:text-slate-700" onClick={() => setOpen(false)} type="button"><AppIcon name="close" size={16} /></button>
            </div>
          </header>

          <div className="max-h-[calc(100dvh-9rem)] space-y-2 overflow-y-auto p-3 sm:max-h-[34rem]">
            {inboxQuery.isLoading ? <p className="py-12 text-center text-xs text-slate-400">در حال دریافت پیام‌ها…</p> : null}
            {inboxQuery.error ? <div className="rounded-2xl border border-rose-200 bg-rose-50 p-3 text-xs font-bold leading-6 text-rose-700">{getErrorMessage(inboxQuery.error)}</div> : null}
            {!inboxQuery.isLoading && !inboxQuery.error && (inboxQuery.data?.items.length ?? 0) === 0 ? (
              <div className="py-12 text-center">
                <span className="mx-auto grid size-12 place-items-center rounded-2xl bg-slate-100 text-slate-400"><AppIcon name="bell" /></span>
                <p className="mt-3 text-xs font-bold text-slate-500">هنوز اعلانی برای سازمان شما ثبت نشده است.</p>
              </div>
            ) : null}

            {inboxQuery.data?.items.map((item) => {
              const style = categoryStyle(item.category, item.notificationType);
              const expanded = expandedId === item.id;
              return (
                <button
                  className={`block w-full rounded-2xl border p-3 text-right transition hover:shadow-sm ${item.isViewed ? 'border-slate-200 bg-white' : style.card}`}
                  key={item.id}
                  onClick={() => openItem(item)}
                  type="button"
                >
                  <span className="flex items-start justify-between gap-3">
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        {!item.isViewed ? <span className="size-2 shrink-0 rounded-full bg-rose-500" /> : null}
                        <span className="truncate text-xs font-black text-slate-900">{item.title}</span>
                      </span>
                      <span className={`${expanded ? 'whitespace-pre-wrap' : 'line-clamp-2'} mt-2 block text-xs leading-6 text-slate-600`}>{item.message}</span>
                    </span>
                    <span className={`shrink-0 rounded-full px-2 py-1 text-[9px] font-black ${style.badge}`}>{style.label}</span>
                  </span>
                  <span className="mt-3 block border-t border-slate-200/70 pt-2 text-[10px] leading-5 text-slate-400">
                    {item.notificationType === 'reservation-requested' ? 'ثبت' : 'ارسال'} در {formatDate(item.sentAtUtc)} توسط {item.sentByDisplayName}
                  </span>
                  <span className="mt-1 block text-[10px] leading-5">
                    {item.isViewed ? (
                      <span className="font-bold text-emerald-700">مشاهده در {formatDate(item.viewedAtUtc)} توسط {item.viewedByDisplayName ?? 'کاربر سازمان'}</span>
                    ) : (
                      <span className="font-bold text-amber-700">برای ثبت مشاهده، اعلان را باز کنید</span>
                    )}
                  </span>
                </button>
              );
            })}
            {viewMutation.error ? <p className="rounded-xl bg-rose-50 px-3 py-2 text-[11px] font-bold text-rose-700">ثبت مشاهده انجام نشد: {getErrorMessage(viewMutation.error)}</p> : null}
          </div>
        </section>
      ) : null}
    </div>
  );
};
